import { getValidNumberWithLocalize, getLocale } from "./utils";

/**
 * numericalFiltering: Function to filter rows based on numerical values and ranges.
 *
 * This function parses and applies various numerical filters, including:
 * - Direct comparisons (e.g., =, !=, <, <=, >, >=)
 * - Ranges:: (e.g., 10 - 20) or negative values (e.g., -10- -20)
 * - Negated ranges (e.g., !(10 - 20))
 *
 * @param {Object} row - The row object that contains cell value.
 * @returns {Boolean} - Returns true if the row matches the filter criteria, otherwise false.
 */
export const numericalFiltering = (row, cellFilter, locale) => {
  const validLocale = getLocale(locale);
  try {
    if (!row || !cellFilter || cellFilter.columnIdx < 0) return false;
    const cell = row[cellFilter.columnIdx];
    if (!cell || !cell.value) return false;

    const rowValue = cell.value;
    const filter = cellFilter.value;
    if (!filter || !filter.length) return true;

    const regExNumber = /(<=|<|>=|>|!=|=)?\s*([+-]?\d*[.,]?\d*|\((\s*[+-]?\d*[.,]?\d*\s*-\s*[+-]?\d*[.,]?\d*)\)|!\((\s*[+-]?\d*[.,]?\d*\s*-\s*[+-]?\d*[.,]?\d*)\)|[+-]?\d+[.,]?\d*\s*-\s*[+-]?\d+[.,]?\d*)\s*/;
    const result = regExNumber.exec(filter);

    if (!result) return false;

    const match = result.input;
    const rowValueNumber = getValidNumberWithLocalize(rowValue, validLocale);
    const filterNumber = getValidNumberWithLocalize(filter, validLocale);

    //regular expression identifies and splits numerical ranges separated by hyphens (e.g., "10 - 20", "0.5-1.5", "-10 - -5")
    const rangeRegex = /(?<=[0-9.])-(?=\s*-?[0-9.])/;

    if (rangeRegex.exec(match)) {
      const [start, end] = match.split(rangeRegex).map((row) => getValidNumberWithLocalize(row, validLocale));
      return match.startsWith('!(')
        ? !(rowValueNumber >= start && rowValueNumber <= end)
        : rowValueNumber >= start && rowValueNumber <= end;
    } else if (filterNumber !== null && rowValueNumber !== null) {
      const [, operator] = result;
      switch (operator) {
        case '=':
          return rowValueNumber === filterNumber;
        case '!=':
          return rowValueNumber !== filterNumber;
        case '<':
          return rowValueNumber < filterNumber;
        case '<=':
          return rowValueNumber <= filterNumber;
        case '>':
          return rowValueNumber > filterNumber;
        case '>=':
          return rowValueNumber >= filterNumber;
        default:
          return rowValue.includes(filter);
      }
    }

    return rowValue.includes(filter);
  } catch (error) {
    console.error('Error in numericalFiltering: ', error);
    return false;
  }
};

/**
 * Filters a row based on the specified cell filter criteria.
 *
 * @param {Object} row - The row object containing cell data.
 * @param {Object} cellFilter - The cell filter object containing filter criteria.
 * @param {string} key - The key attribute of Object to be used for filtering when Array.
 * @returns {boolean} - Returns true if the row matches the filter criteria, otherwise false.
 */

export const textFiltering = (row, cellFilter, key = 'label') => {
  try {
    if (!row || !cellFilter || cellFilter.columnIdx < 0) return false;

    const cell = row[cellFilter.columnIdx];
    if (!cell || !cell.value) return false;

    const cellValues = Array.isArray(cell.value)
      ? cell.value.map(item =>
          typeof item === 'object'
            ? item[key]?.toLowerCase()
            : item.toLowerCase()
        )
      : [cell.value.toLowerCase()];

    const filter = cellFilter.value?.toLowerCase();
    if (!filter || !filter.length) return true;

    const regExString = /^(=|!=|\*|!\*)|~/;
    const operatorMatch = filter.match(regExString);
    if (!operatorMatch)
      return cellValues.some(value => value?.includes(filter));

    const [operator] = operatorMatch;
    const filterValue = filter.replace(operator, '').trim();

    const checkCondition = rowValue => {
      switch (operator) {
        case '=':
          return rowValue === filterValue;
        case '!=':
          return rowValue !== filterValue;
        case '~': {
          const startsWithTilde = filter.startsWith('~');
          const endsWithTilde = filter.endsWith('~');

          if (startsWithTilde && endsWithTilde)
            return rowValue.includes(filterValue.slice(0, -1));
          if (startsWithTilde) return rowValue.startsWith(filterValue);
          if (endsWithTilde) return rowValue.endsWith(filterValue);
          return rowValue.includes(filterValue);
        }
        case '*':
          return rowValue.includes(filterValue);
        case '!*':
          return !rowValue.includes(filterValue);
        default:
          return rowValue.includes(filter);
      }
    };

    return cellValues.some(checkCondition);
  } catch (error) {
    console.error('Error in textFiltering: ', error);
    return false;
  }
};

const getFilters = (cellFilter, options) => ({
  filterInputNumber: row => numericalFiltering(row, cellFilter, options?.locale),
  filterInputText: row => textFiltering(row, cellFilter),
  defaultFiltering: gridHeaders =>
    gridHeaders.reduce((acc, { id, filterTypeHelper }) => {
      if(typeof filterTypeHelper === "string") {
        switch (filterTypeHelper) {
          case 'number':
            acc[id] = (row) => numericalFiltering(row, cellFilter, options?.locale);
            break;
          default:
            acc[id] = (row) => textFiltering(row, cellFilter);
        }
      }

      if(Array.isArray(filterTypeHelper)) {
        const [key] = filterTypeHelper;
        acc[id] = (row) => textFiltering(row, cellFilter, key);
      }

      return acc;
    }, {})
});

export default getFilters;
